/* eslint-disable i18next/no-literal-string */
import {
  Box,
  Button,
  Checkbox,
  Container,
  FileUpload,
  FileUploadReturnType,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@dtx-company/design-system/src'
import { CONTACT_SERVICE_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import { FC, useEffect, useRef, useState } from 'react'
import { IS_CI, IS_DEVELOPMENT } from '@dtx-company/true-common/src/constants/env'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'

enum PrivacyRequestFields {
  REQUEST_TYPE = 'requestType',
  EMAIL = 'email',
  NAME = 'name',
  FILE = 'file',
  CONFIRMED = 'confirmed',
  DETAILS = 'details'
}

interface SendPrivacyRequestData {
  email: string
  requestType: string
  name: string
  file?: File
  confirmed: boolean
  details?: string
}

export const PrivacyRequestContainer: FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const pdfFileInputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const { token } = useAuthState()

  async function sendPrivacyRequest(data: SendPrivacyRequestData): Promise<void> {
    const { email, requestType, name, file, details } = data
    try {
      const formData = new FormData()
      formData.append('email', email)
      formData.append('requestType', requestType)
      if (name) formData.append('name', name)
      if (file) formData.append('pdf', file)
      if (details) formData.append('otherReason', details)

      const res = await fetch(`${CONTACT_SERVICE_ENDPOINT}/privacyrequest`, {
        method: 'POST',
        body: formData,
        headers: {
          authorization: `Bearer ${token}`
        }
      })

      if (res.status === 413) {
        throw Error('File is too big')
      }

      const expectedDevelopmentResponse = res.status === 500 && (IS_DEVELOPMENT || IS_CI)
      if (res.status !== 200 && !expectedDevelopmentResponse) {
        throw Error(res.statusText)
      }

      // Redirect to opt-out page if successful.
      if (requestType === 'delete') {
        router.push({
          pathname: Routes.OPT_OUT
        })
      } else {
        router.push({
          pathname: Routes.PRIVACY_REQUEST_SUBMITTED
        })
      }
    } catch (e) {
      console.error('Error sending privacy request: ', e)
    }
  }

  const {
    register,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
    watch
  } = useForm<SendPrivacyRequestData>({
    defaultValues: {
      name: '',
      email: '',
      file: undefined,
      requestType: '',
      confirmed: false
    }
  })

  useEffect(() => {
    register(PrivacyRequestFields.REQUEST_TYPE, {
      required: true
    })
    register(PrivacyRequestFields.EMAIL, {
      required: true
    })
    register(PrivacyRequestFields.NAME)
    register(PrivacyRequestFields.FILE)
    register(PrivacyRequestFields.CONFIRMED, {
      required: true
    })
    register(PrivacyRequestFields.DETAILS)
  }, [register])

  const watchAll = watch()

  const onSubmit = async (data: SendPrivacyRequestData): Promise<void> => {
    setIsSubmitting(true)

    if (!watchAll.requestType) {
      setError(PrivacyRequestFields.REQUEST_TYPE, {
        type: 'manual',
        message: 'Specify a request type'
      })
    }
    if (!watchAll.confirmed) {
      setError(PrivacyRequestFields.CONFIRMED, {
        type: 'manual',
        message: 'Confirm to submit'
      })
    }

    await sendPrivacyRequest({
      email: data.email,
      requestType: data.requestType,
      name: data.name,
      file: data.file,
      details: data.details,
      confirmed: data.confirmed
    })

    setIsSubmitting(false)
  }

  const showDetailsInput = ['other', 'correct'].includes(watchAll.requestType)

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4} sx={{ mt: 8, mb: 16 }}>
          <Typography variant="body1">
            Welcome to the privacy request page for the dtx company, operator of Flowcode and
            Flowpage.
          </Typography>
          <Typography variant="body1">
            If the privacy or data protection laws where you live give you rights to make requests
            about your personal information, you can use this form to make those requests. For
            example, residents of California, Virginia, Colorado, Connecticut, Nevada, and a growing
            number of other jurisdictions have a right to opt out of what the laws in those states
            call a “sale” of personal information. In most of those states, the law also provides
            for a right to opt out of certain uses or disclosures of personal information for
            certain targeted advertising purposes. We call these the “Sale & Ad Targeting
            Disclosure” opt-out rights. (California law uses the term “sharing” to refer to
            disclosures of personal information for ad targeting that can be stopped with this
            opt-out.) The Sale & Ad Targeting Disclosure opt-out does not stop all disclosures of
            personal information.
          </Typography>
          <Typography variant="body1">
            If you&apos;d like your Sale & Ad Targeting Disclosure opt-out request to cover personal
            information that we collect via cookies and similar technology on our own sites, then
            please click the Cookie Preferences link in the footer below, click Reject All, and then
            click Confirm My Choices. Please repeat this process from each each browser you use to
            visit our site or see content linked from our QR codes.
          </Typography>
          <Typography variant="body1">
            Opt-out requests made using the cookie tools described here may be stored in a cookie,
            so you should repeat the cookie-related opt-outs if you clear your cookies or use a
            browser that automatically clears cookies and want to reactivate the opt-out for that
            browser.
          </Typography>

          <RadioGroup
            formControlProps={{
              color: 'primary'
            }}
            label=""
            onChange={e => {
              setValue(PrivacyRequestFields.REQUEST_TYPE, e.target.value)
            }}
            options={[
              { label: 'Provide me with a copy of my personal information', value: 'provideCopy' },
              { label: 'Correct my personal information', value: 'correct' },
              { label: 'Delete my personal information', value: 'delete' },
              {
                label: 'Sale and ad targeting disclose opt-out (explanation above)',
                value: 'targetOptOut'
              },
              { label: 'Other', value: 'other' }
            ]}
          />

          <Box>
            {Boolean(errors.requestType) && (
              <Typography color="red">You must select a request type</Typography>
            )}
          </Box>

          <TextField
            label="Your name"
            type="text"
            name="name"
            onChange={e => {
              setValue(PrivacyRequestFields.NAME, e.target.value)
            }}
            value={watchAll.name}
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            fullWidth
            required
          />
          <TextField
            label="Your email"
            type="email"
            name="email"
            onChange={e => {
              setValue(PrivacyRequestFields.EMAIL, e.target.value)
            }}
            value={watchAll.email}
            error={Boolean(errors.email)}
            helperText={errors?.email?.message}
            fullWidth
            required
          />

          {showDetailsInput && (
            <TextField
              label="Details: (required)"
              type="text"
              onChange={e => {
                setValue(PrivacyRequestFields.DETAILS, e.target.value)
              }}
              value={watchAll.details || ''}
              error={Boolean(errors.details)}
              helperText={errors.details?.message}
              inputProps={{
                name: 'details',
                maxLength: 64
              }}
              name="details"
              fullWidth
              required={showDetailsInput}
            />
          )}

          <Typography variant="body1">
            We will use this email address to search for data associated with your email address and
            to deliver a response to your request. If you believe we have more than one email
            address on file for you, please submit this form separately for each email address, as
            we may not have linked our records of the two addresses. We may email you to request
            additional verification of your identity before processing your request, so please
            respond.
          </Typography>
          <Typography variant="body1">
            California, Colorado and Connecticut residents may use authorized agents to make their
            requests. If you are an agent and would like to submit a request on behalf of a
            consumer, you must enter their email address above and upload below a copy of the power
            of attorney (or other legally sufficient proof of your authority to act on behalf of the
            individual). For verification purposes, they will need to visit this page themselves if
            their request pertains to data that we have collected through cookies and other
            automated technologies. We will not use agents&apos; websites to process requests.
          </Typography>

          <FileUpload
            label="Upload PDF"
            name={PrivacyRequestFields.NAME}
            accept="application/pdf"
            ref={pdfFileInputRef}
            returnType={FileUploadReturnType.FILE}
            onChange={e => {
              setValue(PrivacyRequestFields.FILE, e ? (e as File) : undefined)
            }}
            placeholder="yourfile.pdf"
            labelAbove
          />

          <Checkbox
            label={
              'By submitting this form, I swear under penalty of perjury that (i) I am legally entitled to submit this form and (ii) if I am submitting this form on behalf of somebody else, I have uploaded legally sufficient proof of my authority to do so.'
            }
            onChange={() => {
              setValue(PrivacyRequestFields.CONFIRMED, !watchAll.confirmed)
            }}
            checked={watchAll.confirmed}
          />

          <Button type="button" onClick={handleSubmit(onSubmit)} loading={isSubmitting}>
            Submit
          </Button>

          {Boolean(errors.confirmed) && (
            <Typography color="red">
              You must click the checkbox to confirm before submitting a request
            </Typography>
          )}
        </Stack>
      </form>
    </Container>
  )
}
